import { useOrgContext } from "@components/layout/OrgContext";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AiEvaluationReport,
  AiEvaluationSummary,
} from "@io-spoke/contracts/aiFeedback";
import { BrainCircuit, LoaderCircle } from "lucide-react";
import { PageShell } from "@components/layout/PageShell";
import { PageHeader } from "@components/layout/PageHeader";
import { SidebarTrigger } from "@components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { Button } from "@components/ui/button";
import {
  getAiEvaluationSummary,
  getLatestAiEvaluationReport,
} from "../lib/aiFeedback";

function percent(value: number | null) {
  return value === null ? "Not enough data" : `${Math.round(value * 100)}%`;
}

function recommendationCopy(report: AiEvaluationReport) {
  if (report.recommendation === "candidate_better")
    return "The candidate met the measured quality and segment-regression gates.";
  if (report.recommendation === "baseline_better")
    return "The released pipeline remains stronger on this evaluation set.";
  return "More representative labeled cases are required before comparing pipelines.";
}

function QualityMetric({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
}

function EvaluationReport({ report }: { report: AiEvaluationReport }) {
  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-base">
          Latest controlled evaluation
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <p className="text-xs text-muted-foreground">Evaluation set</p>
            <p className="mt-1 font-medium">
              {report.datasetVersion} · {report.candidate.cases} cases
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Candidate quality</p>
            <p className="mt-1 font-medium">
              {percent(report.candidate.accuracy)} correct
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Release decision</p>
            <p className="mt-1 font-medium capitalize">{report.decision}</p>
          </div>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          {recommendationCopy(report)}
        </p>
      </CardContent>
    </Card>
  );
}

function PipelineQuality({
  data,
  navigate,
}: {
  data: AiEvaluationSummary;
  navigate?: (path: string) => void;
}) {
  return (
    <section className="mt-6">
      <h2 className="text-base font-semibold">
        IO Spoke intelligence pipeline
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Evaluation data stays separated by pipeline version, so improvements can
        be measured safely.
      </p>
      <div className="mt-3 space-y-3">
        {data.configurations.map((item) => (
          <Card key={item.key}>
            <CardContent className="grid gap-4 pt-5 md:grid-cols-[1fr_auto_auto_auto]">
              <div>
                <p className="font-medium">IO Spoke Intelligence Pipeline</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Version {item.pipelineVersion}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Reviewed</p>
                <p className="font-semibold">
                  {item.rated} / {item.recommendations}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Abstained</p>
                <p className="font-semibold">{item.abstentions}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Useful</p>
                <p className="font-semibold">{percent(item.positiveRate)}</p>
              </div>
            </CardContent>
          </Card>
        ))}
        {data.configurations.length === 0 ? (
          <div className="rounded-lg border border-dashed py-14 text-center">
            <BrainCircuit
              className="mx-auto h-6 w-6 text-muted-foreground"
              aria-hidden="true"
            />
            <h2 className="mt-3 font-semibold">No quality data yet</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Analyze customer evidence and rate the proposal to begin measuring
              quality.
            </p>
            {navigate ? (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate("/rescues")}
              >
                Open Evidence Lab
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function AiEvaluationPage({
  navigate,
}: {
  navigate?: (path: string) => void;
}) {
  const { currentOrg } = useOrgContext();
  return (
    <WorkspaceEvaluation key={currentOrg?.id ?? "none"} navigate={navigate} />
  );
}

function WorkspaceEvaluation({
  navigate,
}: {
  navigate?: (path: string) => void;
}) {
  const { currentOrg, currentMembership } = useOrgContext();
  const canViewAdvanced =
    currentMembership?.orgId === currentOrg?.id &&
    currentMembership?.status === "active" &&
    ["owner", "admin"].includes(currentMembership.role);
  const [data, setData] = useState<AiEvaluationSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<AiEvaluationReport | null>(null);
  const loadAction = useAsyncAction();
  const mounted = useRef(true);
  const generation = useRef(0);
  const load = useCallback(
    () =>
      loadAction.run(async () => {
        const request = ++generation.current;
        const current = () => mounted.current && request === generation.current;
        try {
          const [summary, latest] = await Promise.all([
            getAiEvaluationSummary(),
            getLatestAiEvaluationReport(),
          ]);
          if (!current()) return;
          setData(summary);
          setReport(latest);
          setError(null);
        } catch {
          if (current()) setError("AI quality data could not be loaded.");
        }
      }),
    [loadAction.run],
  );
  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        leading={<SidebarTrigger />}
        eyebrow="Intelligence quality"
        title="AI Quality"
        description="Review how people rated IO Spoke recommendations in this workspace. Use feedback and corrections to decide what needs closer human review."
      />
      {!data && !error ? (
        <div
          className="mt-12 flex items-center justify-center gap-2 text-sm text-muted-foreground"
          role="status"
        >
          <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          Loading quality data…
        </div>
      ) : null}
      {error ? (
        <Card className="mt-8 border-destructive/40">
          <CardContent className="pt-5">
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => void load()}
              loading={loadAction.pending}
              loadingLabel="Loading quality data…"
            >
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : null}
      {data ? (
        <>
          <section
            className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
            aria-label="AI quality summary"
          >
            <QualityMetric
              label="Recommendations"
              value={data.recommendations}
            />
            <QualityMetric label="No recommendation" value={data.abstentions} />
            <QualityMetric label="Reviewed" value={data.rated} />
            <QualityMetric
              label="Rated useful"
              value={percent(data.positiveRate)}
            />
            <QualityMetric label="Corrections" value={data.corrections} />
          </section>
          <p className="mt-4 text-sm text-muted-foreground">
            Ratings describe the recommendations people reviewed, not overall
            accuracy or a guarantee of future results.
          </p>
          {navigate ? (
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/rescues")}
            >
              Review customer evidence
            </Button>
          ) : null}
          {canViewAdvanced ? (
            <details className="mt-6 rounded-lg border p-4">
              <summary className="min-h-11 cursor-pointer text-sm font-medium focus-visible:outline focus-visible:outline-2">
                Advanced evaluation
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">
                Administrator evaluation records compare specific reviewed cases
                and versions. A recorded release decision is not a quality
                guarantee.
              </p>
              {report ? <EvaluationReport report={report} /> : null}
              <PipelineQuality data={data} navigate={navigate} />
            </details>
          ) : null}
        </>
      ) : null}
    </PageShell>
  );
}
